import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import {
  Settings as SettingsIcon, Save, Database,
  Monitor, Download, Upload, RefreshCw, Trash2, X, User
} from 'lucide-react';
import { MAX_NICKNAME_LENGTH, sanitizeNickname } from '../utils/profile';

function Settings({ profile, onUpdate, onClose, onReset }) {
  const { t } = useLanguage();

  const [activeTab, setActiveTab] = useState('general');


  const [localSettings, setLocalSettings] = useState(profile.settings || {
    background: 'bg-[#050505]'
  });

  const [operatorName, setOperatorName] = useState(sanitizeNickname(profile.nickname));


  const backgrounds = [
    { id: 'default', name: 'bg_default', value: 'bg-[#050505]', label: 'Pure Black' },
    { id: 'tactical', name: 'bg_tactical', value: 'bg-zinc-950', label: 'Tactical Zinc' },
    { id: 'midnight', name: 'bg_midnight', value: 'bg-[#0f172a]', label: 'Deep Navy' },
    { id: 'forest', name: 'bg_forest', value: 'bg-[#022c22]', label: 'Dark Forest' },
  ];

  const handleSave = () => {
    const nickname = sanitizeNickname(operatorName);
    const updatedProfile = {
      ...profile,
      nickname,
      settings: localSettings
    };

    // LocalStorage güncelle
    localStorage.setItem('tarkovProfile', JSON.stringify(updatedProfile));

    // Ana uygulamayı güncelle (Bu sayede Navbar'daki isim ve Arkaplan değişecek)
    onUpdate(updatedProfile);
    onClose();
  };

  const handleExport = () => {
    try {
      const dataStr = JSON.stringify(profile, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      const fileSafeName = sanitizeNickname(operatorName).replace(/[^a-z0-9_-]/gi, '-').toLowerCase();
      link.href = url;
      link.download = `tarkovstash-backup-${fileSafeName}-${new Date().toISOString().slice(0, 10)}.json`;
      link.click();
    } catch (err) {
      alert("Export failed!");
    }
  };

  const handleImport = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const importedProfile = JSON.parse(e.target.result);

          // Basit bir doğrulama (Profile yapısına benziyor mu?)
          if (!importedProfile.gameEdition && !importedProfile.level) {
            throw new Error("Invalid file format");
          }

          localStorage.setItem('tarkovProfile', JSON.stringify(importedProfile));
          onUpdate(importedProfile);
          alert(t('success_import'));
          onClose();
        } catch (error) {
          alert(t('error_import'));
        }
      };
      reader.readAsText(file);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-fadeIn">

      {/* Ayarlar Penceresi */}
      <div className="bg-[#09090b] border border-zinc-800 rounded-sm w-full max-w-4xl h-[600px] shadow-2xl flex flex-col overflow-hidden relative">

        {/* Dekoratif Arkaplan (Trader Havası) */}
        <div
          className="absolute inset-0 pointer-events-none opacity-5 bg-no-repeat bg-right-bottom bg-contain z-0"
          style={{ backgroundImage: "url('https://assets.tarkov.dev/trader-mechanic.jpg')" }}
        ></div>

        {/* HEADER */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-800 bg-[#0c0c0e] relative z-10">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-zinc-800 rounded-sm border border-zinc-700">
              <SettingsIcon className="w-5 h-5 text-zinc-400" />
            </div>
            <h2 className="text-xl font-bold text-white tracking-wide uppercase font-mono">{t('settings_title')}</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-zinc-800 rounded-sm text-zinc-500 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* CONTENT LAYOUT */}
        <div className="flex flex-1 overflow-hidden relative z-10">

          {/* SIDEBAR */}
          <div className="w-64 bg-[#0c0c0e] border-r border-zinc-800 p-4 space-y-2">
            <button
              onClick={() => setActiveTab('general')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-bold transition-all ${activeTab === 'general'
                  ? 'bg-zinc-800 text-white border-l-4 border-emerald-500'
                  : 'text-zinc-500 hover:bg-zinc-900 hover:text-zinc-300'
                }`}
            >
              <Monitor className="w-4 h-4" />
              {t('tab_general')}
            </button>

            <button
              onClick={() => setActiveTab('data')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-bold transition-all ${activeTab === 'data'
                  ? 'bg-zinc-800 text-white border-l-4 border-orange-500'
                  : 'text-zinc-500 hover:bg-zinc-900 hover:text-zinc-300'
                }`}
            >
              <Database className="w-4 h-4" />
              {t('tab_data')}
            </button>
          </div>

          {/* MAIN PANEL */}
          <div className="flex-1 p-8 overflow-y-auto custom-scrollbar">

            {/* --- GENERAL TAB --- */}
            {activeTab === 'general' && (
              <div className="space-y-8 animate-fadeIn">

                {/* Operator Name */}
                <div className="bg-zinc-900/50 p-6 rounded-sm border border-zinc-800">
                  <h3 className="text-white font-bold mb-2 flex items-center gap-2 text-sm uppercase tracking-wider">
                    <User className="w-4 h-4 text-emerald-500" /> {t('operator_identity')}
                  </h3>
                  <p className="text-zinc-500 text-xs mb-4 font-mono">{t('operator_name_desc')}</p>
                  <input
                    type="text"
                    value={operatorName}
                    maxLength={MAX_NICKNAME_LENGTH}
                    onChange={(e) => setOperatorName(sanitizeNickname(e.target.value, ''))}
                    placeholder={t('operator_placeholder')}
                    className="w-full bg-black border border-zinc-700 rounded-sm px-4 py-3 text-white focus:outline-none focus:border-emerald-500 transition-colors font-mono font-bold uppercase"
                  />
                  <div className="mt-2 text-[10px] text-zinc-500 font-mono text-right">
                    {operatorName.length}/{MAX_NICKNAME_LENGTH}
                  </div>
                </div>

                {/* Backgrounds */}
                <div className="bg-zinc-900/50 p-6 rounded-sm border border-zinc-800">
                  <h3 className="text-white font-bold mb-4 flex items-center gap-2 text-sm uppercase tracking-wider">
                    <Monitor className="w-4 h-4 text-emerald-500" /> {t('background_style')}
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    {backgrounds.map(bg => (
                      <button
                        key={bg.id}
                        onClick={() => setLocalSettings({ ...localSettings, background: bg.value })}
                        className={`relative h-16 rounded-sm overflow-hidden border-2 transition-all group ${localSettings.background === bg.value
                            ? 'border-emerald-500 shadow-lg scale-[1.02]'
                            : 'border-zinc-700 hover:border-zinc-500 opacity-60 hover:opacity-100'
                          }`}
                      >
                        {/* Renk Önizlemesi */}
                        <div className={`absolute inset-0 ${bg.value}`}></div>

                        {/* İsim Etiketi */}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-white text-[10px] uppercase font-bold bg-black/70 px-2 py-1 rounded-sm backdrop-blur-sm shadow-sm font-mono">
                            {bg.label}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* --- DATA TAB --- */}
            {activeTab === 'data' && (
              <div className="space-y-8 animate-fadeIn">

                {/* Backup & Restore */}
                <div className="bg-zinc-900/50 p-6 rounded-sm border border-zinc-800">
                  <h3 className="text-white font-bold mb-4 flex items-center gap-2 text-sm uppercase tracking-wider">
                    <Database className="w-4 h-4 text-orange-500" /> {t('export_import')}
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <button onClick={handleExport} className="bg-black border border-zinc-700 hover:border-blue-500 p-4 rounded-sm text-left transition-all group">
                      <Download className="w-6 h-6 text-blue-500 mb-2 group-hover:scale-110 transition-transform" />
                      <div className="font-bold text-white text-sm uppercase">{t('export_btn')}</div>
                      <div className="text-[10px] text-zinc-500 mt-1 font-mono">{t('export_desc')}</div>
                    </button>
                    <label className="bg-black border border-zinc-700 hover:border-emerald-500 p-4 rounded-sm text-left transition-all group cursor-pointer">
                      <Upload className="w-6 h-6 text-emerald-500 mb-2 group-hover:scale-110 transition-transform" />
                      <div className="font-bold text-white text-sm uppercase">{t('import_btn')}</div>
                      <div className="text-[10px] text-zinc-500 mt-1 font-mono">{t('import_desc')}</div>
                      <input type="file" accept=".json" onChange={handleImport} className="hidden" />
                    </label>
                  </div>
                </div>

                {/* Reset Zones */}
                <div className="bg-red-900/5 p-6 rounded-sm border border-red-900/20">
                  <h3 className="text-red-500 font-bold mb-4 flex items-center gap-2 text-sm uppercase tracking-wider">
                    <Trash2 className="w-4 h-4" /> {t('reset_zone')}
                  </h3>
                  <div className="space-y-3">
                    {/* Soft Reset */}
                    <div className="flex items-center justify-between bg-black border border-zinc-800 p-4 rounded-sm">
                      <div>
                        <div className="text-zinc-300 font-bold text-sm uppercase">{t('reset_session_title')}</div>
                        <div className="text-[10px] text-zinc-500 font-mono">{t('reset_session_desc')}</div>
                      </div>
                      <button
                        onClick={() => {
                          if (window.confirm(t('confirm_reset'))) {
                            const resetProfile = { ...profile, completedQuests: [], completedObjectives: {}, hideoutModules: {} };
                            localStorage.setItem('tarkovProfile', JSON.stringify(resetProfile));
                            localStorage.removeItem('story_guide_progress');
                            onUpdate(resetProfile);
                          }
                        }}
                        className="px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-white text-[10px] font-bold rounded-sm border border-zinc-600 transition-colors flex items-center gap-2 uppercase"
                      >
                        <RefreshCw className="w-3 h-3" /> {t('reset_session_btn')}
                      </button>
                    </div>

                    {/* Hard Wipe */}
                    <div className="flex items-center justify-between bg-black border border-red-900/30 p-4 rounded-sm">
                      <div>
                        <div className="text-red-500 font-bold text-sm uppercase">{t('wipe_profile_title')}</div>
                        <div className="text-[10px] text-red-900/80 font-mono">{t('wipe_profile_desc')}</div>
                      </div>
                      <button
                        onClick={() => {
                          if (window.confirm(t('confirm_wipe'))) {
                            onReset();
                            onClose();
                          }
                        }}
                        className="px-3 py-2 bg-red-900/20 hover:bg-red-900/40 text-red-500 text-[10px] font-bold rounded-sm border border-red-900/50 transition-colors flex items-center gap-2 uppercase"
                      >
                        <Trash2 className="w-3 h-3" /> {t('wipe_profile_btn')}
                      </button>
                    </div>
                  </div>
                </div>

              </div>
            )}

          </div>
        </div>

        {/* FOOTER */}
        <div className="px-6 py-4 border-t border-zinc-800 bg-[#0c0c0e] flex justify-end gap-3 relative z-10">
          <button
            onClick={onClose}
            className="px-5 py-2.5 rounded-sm font-bold text-sm text-zinc-500 hover:text-white hover:bg-zinc-800 transition-all uppercase"
          >
            {t('cancel')}
          </button>
          <button
            onClick={handleSave}
            className="px-6 py-2.5 rounded-sm font-bold text-sm bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg shadow-emerald-900/20 flex items-center gap-2 transition-all uppercase"
          >
            <Save className="w-4 h-4" />
            {t('save_changes')}
          </button>
        </div>

      </div>
    </div>
  );
}

export default Settings;
